import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { compressImageToDataURL } from "@/lib/image";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Newspaper, Image as ImageIco, ImageOff, Megaphone, Upload, GalleryHorizontal, Check, X, Video, Pin, PinOff, Handshake, User, Trophy, Shield } from "lucide-react";
import { useCardExpansion } from "@/hooks/useCardExpansion";
import { CardThumb } from "@/components/cards/CardThumb";

const empty = { title: "", summary: "", body: "", image_url: "", video_url: "", image_orientation: "portrait", author: "", published: true, published_at: "", kind: "news", show_on_landing: false, pinned: false, pin_expires_at: "" };
const MAX_IMG_BYTES = 5 * 1024 * 1024; // 5MB

function toLocalInput(iso) {
  if (!iso) return "";
  try { const d = new Date(iso); const off = d.getTimezoneOffset(); return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16); } catch { return ""; }
}
function toISO(localStr) {
  if (!localStr) return "";
  try { return new Date(localStr).toISOString(); } catch { return localStr; }
}
function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  } catch { return iso; }
}

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [dragOverImage, setDragOverImage] = useState(false);
  const [dragOverVideo, setDragOverVideo] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const { isExpanded, toggle: toggleImage } = useCardExpansion();
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const load = async () => { const { data } = await api.get("/news/admin/all"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm({ ...empty, published_at: toLocalInput(new Date().toISOString()) }); setOpen(true); };
  const startEdit = (n) => { setEditing(n); setForm({ ...empty, ...n, published_at: toLocalInput(n.published_at) }); setOpen(true); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Image must be 15MB or smaller"); return; }

    try {
      const img = await new Promise((resolve, reject) => {
        const i = new window.Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = URL.createObjectURL(f);
      });
      URL.revokeObjectURL(img.src);

      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (w !== 1080 || h !== 1350) {
        toast.error(`Images must be exactly 1080 × 1350 px (portrait). This image is ${w} × ${h} px.`);
        return;
      }

      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      setForm((s) => ({ ...s, image_url: dataUrl }));
    } catch {
      toast.error("Could not read the file");
    }
  };

  const onVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    const ext = (f.name || "").split(".").pop().toLowerCase();
    const videoExts = ["mp4", "webm", "mov", "avi", "mkv", "m4v", "mpeg", "mpg", "wmv"];
    if (!f.type.startsWith("video/") && !videoExts.includes(ext)) { toast.error("Please pick a video file"); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("Video must be 50MB or smaller"); return; }
    const reader = new FileReader();
    const dataUrl = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });
    setForm((s) => ({ ...s, video_url: dataUrl }));
    toast.success("Video uploaded");
  };

  const clearImage = () => {
    setForm((s) => ({ ...s, image_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const clearVideo = () => {
    setForm((s) => ({ ...s, video_url: "" }));
    if (videoRef.current) videoRef.current.value = "";
  };

  const pickFromGallery = (url) => {
    setForm((s) => ({ ...s, image_url: url }));
    setGalleryOpen(false);
  };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    const payload = { ...form, published_at: form.published_at ? toISO(form.published_at) : "" };
    try {
      if (editing) await api.put(`/news/${editing.id}`, payload);
      else await api.post("/news", payload);
      toast.success("News saved");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete this news item?")) return; await api.delete(`/news/${id}`); toast.success("Deleted"); load(); };

  const togglePin = async (n) => {
    const nowPinned = !n.pinned;
    try {
      await api.put(`/news/${n.id}`, { ...n, pinned: nowPinned, pin_expires_at: nowPinned ? (n.pin_expires_at || "") : "" });
      toast.success(nowPinned ? "Pinned to first position" : "Unpinned");
      load();
    } catch { toast.error("Failed to update pin"); }
  };

  return (
    <AppShell variant="admin" title="News" actions={
      <Button onClick={startNew} className="btn-accent-modern h-10" data-testid="news-new"><Plus className="h-4 w-4 mr-2" /> Add news</Button>
    }>
      {items.length === 0 ? (
        <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
          <Newspaper className="mx-auto h-8 w-8 mb-3 opacity-60" /> No news yet. Click <span className="font-medium text-foreground">Add news</span> to publish one.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const showImage = isExpanded(n.id);
            return (
            <Card key={n.id} className="overflow-hidden rounded-2xl hover-lift transition-all">
              {/* Full-width image reveals only when the admin clicks
                  "Show full image"; the compact left-side thumbnail below
                  is always visible so the news feed stays scannable. */}
              {showImage && n.image_url && (
                <div className="aspect-[21/9] max-h-[420px] bg-[hsl(var(--secondary))]">
                  <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex items-stretch gap-4 p-4">
                {!showImage && (<CardThumb
                  src={n.image_url}
                  alt={n.title}
                  className="h-24 w-32 sm:h-28 sm:w-40 rounded-xl"
                />)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[10px] font-mono-sm uppercase tracking-[0.18em] text-muted-foreground">{fmt(n.published_at)} {n.author ? ` · ${n.author}` : ""}</div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {n.pinned && <Badge className="text-[10px] bg-[#1E3A8A] text-white hover:bg-[#1E3A8A]/90"><Pin className="h-2.5 w-2.5 mr-1" />Pinned</Badge>}
                      {!n.published && <Badge variant="outline" className="text-[10px]">Draft</Badge>}
                    </div>
                  </div>
                  <div className="mt-1 text-lg font-semibold tracking-tight">{n.title}</div>
                  {n.summary && <div className="mt-1 text-sm text-muted-foreground line-clamp-2">{n.summary}</div>}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => startEdit(n)} data-testid={`news-edit-${n.id}`}><Edit className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    <Button
                      size="sm"
                      variant={n.pinned ? "default" : "outline"}
                      className={`rounded-full ${n.pinned ? "bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" : ""}`}
                      onClick={() => togglePin(n)}
                      data-testid={`news-pin-${n.id}`}
                      title={n.pinned ? "Unpin from first position" : "Pin to first position"}
                    >
                      {n.pinned ? <><PinOff className="h-3.5 w-3.5 mr-1" /> Unpin</> : <><Pin className="h-3.5 w-3.5 mr-1" /> Pin</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="hidden rounded-full text-muted-foreground hover:text-foreground"
                      onClick={() => toggleImage(n.id)}
                      data-testid={`news-toggle-image-${n.id}`}
                      aria-expanded={showImage}
                      title={showImage ? "Hide image" : "Show full image"}
                    >
                      {showImage ? (
                        <><ImageOff className="h-3.5 w-3.5 mr-1" /> Hide image</>
                      ) : (
                        <><ImageIco className="h-3.5 w-3.5 mr-1" /> Show full image</>
                      )}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(n.id)} data-testid={`news-del-${n.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>{editing ? "Edit news" : "New news"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pt-6 pb-6">
            <div className="space-y-4">
            <div><Label className="text-sm font-medium">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="news-form-title" /></div>
            <div><Label className="text-sm font-medium">Summary <span className="text-muted-foreground font-normal">(shown on dashboard)</span></Label>
              <div className="flex items-end gap-2 mt-1.5">
                <Textarea rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} className="rounded-lg flex-1" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 h-9 text-xs gap-1.5"
                  data-testid="news-auto-summary-btn"
                  disabled={!form.body || form.body.trim().length < 10}
                  onClick={() => {
                    const body = (form.body || "").trim();
                    if (!body) return;
                    // Split into sentences, take first 2-3 that fit ~160 chars
                    const sentences = body.split(/(?<=[.!?])\s+/).filter(s => s.length > 5);
                    let summary = "";
                    for (const s of sentences) {
                      const candidate = summary ? summary + " " + s : s;
                      if (candidate.length > 180 && summary) break;
                      summary = candidate;
                      if (summary.length >= 120) break;
                    }
                    // Fallback: just truncate if no sentence breaks
                    if (!summary) summary = body.slice(0, 160).replace(/\s+\S*$/, "") + "…";
                    set("summary", summary);
                  }}
                >
                  <Newspaper className="h-3.5 w-3.5" /> Auto
                </Button>
              </div>
            </div>
            <div><Label className="text-sm font-medium">Body</Label><Textarea rows={5} value={form.body} onChange={(e) => set("body", e.target.value)} className="mt-1.5 rounded-lg" placeholder="Full article text" /></div>

            {/* Image picker */}
            <div>
              <Label className="text-sm font-medium">Image</Label>
              <div
                className={`mt-1.5 border border-dashed rounded-xl p-4 text-center transition-colors ${dragOverImage ? "border-primary bg-primary/10" : "border-border bg-[hsl(var(--secondary))]/40"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverImage(true); }}
                onDragLeave={() => setDragOverImage(false)}
                onDrop={(e) => { e.preventDefault(); setDragOverImage(false); const f = e.dataTransfer.files?.[0]; if (f) onFile({ target: { files: [f] } }); }}
              >
                <input ref={fileRef} onChange={onFile} type="file" accept="image/png,.png" className="hidden" data-testid="news-image-input" />
                {form.image_url ? (
                  <div className="relative inline-block">
                    <img src={form.image_url} alt="preview" className="max-h-48 mx-auto rounded-lg object-cover" data-testid="news-image-preview" />
                    <button type="button" onClick={clearImage} className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-background border border-border shadow-md inline-flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive transition-colors" aria-label="Remove image" data-testid="news-image-clear">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="py-5">
                    <ImageIco className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <div className="text-sm text-muted-foreground">Drag & drop an image or pick from gallery</div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {form.image_orientation === "portrait"
                        ? "PNG, JPG, WebP · must be exactly 1080 × 1350 px (no compression) · GIF any size"
                        : "PNG, JPG, WebP · must be exactly 1920 × 1080 px (16:9, no compression) · GIF any size"}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2 justify-center">
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="news-image-upload-btn">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {form.image_url ? "Replace" : "Upload image"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setGalleryOpen(true)} data-testid="news-image-gallery-btn">
                    <GalleryHorizontal className="h-3.5 w-3.5 mr-1.5" /> Choose from gallery
                  </Button>
                </div>
              </div>

              {/* Image orientation — portrait only */}
            </div>

            {/* Video picker */}
            <div>
              <Label className="text-sm font-medium">Video</Label>
              <div
                className={`mt-1.5 border border-dashed rounded-xl p-4 text-center transition-colors ${dragOverVideo ? "border-primary bg-primary/10" : "border-border bg-[hsl(var(--secondary))]/40"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverVideo(true); }}
                onDragLeave={() => setDragOverVideo(false)}
                onDrop={(e) => { e.preventDefault(); setDragOverVideo(false); const f = e.dataTransfer.files?.[0]; if (f) onVideoFile({ target: { files: [f] } }); }}
              >
                <input ref={videoRef} onChange={onVideoFile} type="file" accept="video/*,.mp4,.webm,.mov,.avi,.mkv,.m4v" className="hidden" data-testid="news-video-input" />
                {form.video_url ? (
                  <div className="relative inline-block">
                    <video src={form.video_url} controls className="max-h-48 mx-auto rounded-lg" data-testid="news-video-preview" />
                    <button type="button" onClick={clearVideo} className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-background border border-border shadow-md inline-flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive transition-colors" aria-label="Remove video" data-testid="news-video-clear">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="py-5">
                    <Video className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <div className="text-sm text-muted-foreground">Drag & drop a video or click upload</div>
                    <div className="text-[11px] text-muted-foreground mt-1">MP4, WebM, MOV · any resolution · up to 50MB</div>
                  </div>
                )}
                <div className="mt-3">
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => videoRef.current?.click()} data-testid="news-video-upload-btn">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {form.video_url ? "Replace video" : "Upload video"}
                  </Button>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm font-medium">Author</Label><Input value={form.author} onChange={(e) => set("author", e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
              <div><Label className="text-sm font-medium">Published at</Label><Input type="datetime-local" value={form.published_at} onChange={(e) => set("published_at", e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Published</Label>
              <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <Label className="text-sm font-medium">Type</Label>
              <Select value={form.kind || "news"} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger className="h-10 w-40 rounded-lg" data-testid="news-form-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">
                    <span className="inline-flex items-center gap-2"><Newspaper className="h-3.5 w-3.5" /> News</span>
                  </SelectItem>
                  <SelectItem value="announcement">
                    <span className="inline-flex items-center gap-2"><Megaphone className="h-3.5 w-3.5" /> Announcement</span>
                  </SelectItem>
                  <SelectItem value="sponsorship">
                    <span className="inline-flex items-center gap-2"><Handshake className="h-3.5 w-3.5" /> Sponsorship</span>
                  </SelectItem>
                  <SelectItem value="player">
                    <span className="inline-flex items-center gap-2"><User className="h-3.5 w-3.5" /> Player</span>
                  </SelectItem>
                  <SelectItem value="match">
                    <span className="inline-flex items-center gap-2"><Trophy className="h-3.5 w-3.5" /> Match</span>
                  </SelectItem>
                  <SelectItem value="club">
                    <span className="inline-flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> Club</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-sm font-medium">Show on landing page</Label>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Featured on the public homepage. (Rendering not wired yet.)
                </div>
              </div>
              <Switch
                checked={!!form.show_on_landing}
                onCheckedChange={(v) => set("show_on_landing", v)}
                data-testid="news-form-show-on-landing"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-sm font-medium flex items-center gap-1.5"><Pin className="h-3.5 w-3.5" /> Pin to first position</Label>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Pinned news stays first in the landing strip until unpinned or expiry date passes.
                </div>
              </div>
              <Switch
                checked={!!form.pinned}
                onCheckedChange={(v) => set("pinned", v)}
                data-testid="news-form-pinned"
              />
            </div>
            {form.pinned && (
              <div>
                <Label className="text-sm font-medium">Pin expires at <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input type="datetime-local" value={form.pin_expires_at ? toLocalInput(form.pin_expires_at) : ""} onChange={(e) => set("pin_expires_at", e.target.value ? toISO(e.target.value) : "")} className="mt-1.5 h-11 rounded-lg" data-testid="news-form-pin-expires" />
                <p className="mt-1 text-[11px] text-muted-foreground">Leave blank to pin indefinitely until manually unpinned.</p>
              </div>
            )}
            <Button onClick={save} className="w-full h-11 btn-primary-modern" data-testid="news-form-save">Save news</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GalleryPickerDialog open={galleryOpen} onOpenChange={setGalleryOpen} currentUrl={form.image_url} onSelect={pickFromGallery} />
    </AppShell>
  );
}

// Gallery photo picker — shows approved club gallery images in a grid so the
// admin can drop one into the news/announcement form without having to type
// a URL. Mirrors the picker used on the Events form.
function GalleryPickerDialog({ open, onOpenChange, currentUrl, onSelect }) {
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/gallery", { params: { status: "approved" } });
        if (alive) setPhotos(data || []);
      } catch {
        if (alive) setPhotos([]);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl">
        <DialogHeader><DialogTitle>Pick from the club gallery</DialogTitle></DialogHeader>
        {photos === null ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Loading gallery…</div>
        ) : photos.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">
            <ImageIco className="h-8 w-8 mx-auto mb-2 opacity-60" />
            No approved gallery photos yet. Approve photos in the Gallery panel first.
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="columns-2 sm:columns-3 gap-3" data-testid="news-gallery-picker-grid">
              {photos.map((g) => {
                const isSelected = currentUrl && currentUrl === g.image_url;
                return (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => onSelect(g.image_url)}
                    className={`group relative mb-3 w-full break-inside-avoid rounded-xl overflow-hidden border-2 transition-all ${isSelected ? "border-[hsl(var(--accent))] ring-2 ring-[hsl(var(--accent))]/40" : "border-transparent hover:border-[hsl(var(--accent))]/60"}`}
                    data-testid={`news-gallery-photo-${g.id}`}
                  >
                    <img src={g.image_url} alt={g.title || "Gallery photo"} className="w-full h-auto block group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    {isSelected && (
                      <div className="absolute top-2 right-2 h-7 w-7 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center shadow-md">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                    {g.title && (
                      <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 text-[11px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {g.title}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
